/* product-title-tooltip.js
   Tooltip on truncated product titles (desktop only).
   Lightweight: one tooltip element + event delegation.
*/
(() => {
  const mqDesktop = window.matchMedia("(min-width: 990px)");
  const mqHover = window.matchMedia("(hover: hover) and (pointer: fine)");

  // Optional scope to reduce work (collection/blog/article only)
  const template = document.documentElement?.dataset?.template || "";
  const shouldRun =
    /collection|blog|article|search|index/i.test(template) ||
    document.querySelector(".card__heading .full-unstyled-link");

  if (!shouldRun) return;

  let tooltipEl = null;
  let activeTarget = null;
  let rafId = null;

  const ensureTooltip = () => {
    if (tooltipEl) return tooltipEl;
    tooltipEl = document.createElement("div");
    tooltipEl.className = "mpr-title-tooltip";
    tooltipEl.setAttribute("role", "tooltip");
    tooltipEl.hidden = true;
    document.body.appendChild(tooltipEl);
    return tooltipEl;
  };

  const isTruncated = (el) => {
    // Works for desktop nowrap ellipsis case
    // small epsilon avoids false positives due to subpixel rounding
    return el.scrollWidth > el.clientWidth + 1;
  };

  const positionTooltip = (target) => {
    const t = ensureTooltip();
    const r = target.getBoundingClientRect();

    // Prefer below the title, but keep inside viewport
    const margin = 8;
    const maxWidth = 360;

    // Measure with content first
    t.style.maxWidth = `${maxWidth}px`;

    // Compute left/top with viewport clamping
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;

    // Temporarily unhide for measurement
    t.hidden = false;
    t.style.opacity = "0";
    t.style.left = "0px";
    t.style.top = "0px";

    const tw = t.offsetWidth;
    const th = t.offsetHeight;

    let left = r.left;
let top = r.top + (r.height / 2) - (th / 2);

    // Clamp horizontally
    if (left + tw + margin > viewportW) left = Math.max(margin, viewportW - tw - margin);
    if (left < margin) left = margin;

    // If not enough room below, place above
    if (top + th + margin > viewportH) {
      top = r.top - th - margin;
    }
    if (top < margin) top = margin;

    // Use fixed positioning to avoid scroll parent issues
    t.style.position = "fixed";
    t.style.left = `${left}px`;
    t.style.top = `${top}px`;
    t.style.opacity = "1";
  };

  const show = (target) => {
    if (!mqDesktop.matches || !mqHover.matches) return;

    const link = target.closest(".card__heading .full-unstyled-link");
    if (!link) return;

    // Only if truncated
    if (!isTruncated(link)) return;

    activeTarget = link;
    const t = ensureTooltip();

    // Use aria-label if present, else textContent
    const fullText = (link.getAttribute("aria-label") || link.textContent || "").trim();
    if (!fullText) return;

    t.textContent = fullText;

    // Defer positioning to next frame (ensures correct sizes)
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => positionTooltip(link));
  };

  const hide = () => {
    if (!tooltipEl) return;
    tooltipEl.hidden = true;
    tooltipEl.style.opacity = "0";
    activeTarget = null;
  };

  // Event delegation (single listeners)
  document.addEventListener("mouseover", (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const link = target.closest(".card__heading .full-unstyled-link");
    if (!link) return;

    // Avoid repeated work when moving inside same link
    if (activeTarget === link) return;

    show(link);
  });

  document.addEventListener("mouseout", (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const link = target.closest(".card__heading .full-unstyled-link");
    if (!link) return;

    // If moving to tooltip itself, ignore (we set pointer-events none in CSS anyway)
    hide();
  });

  // Hide on scroll/resize (prevents “floating” wrong position)
  window.addEventListener("scroll", hide, { passive: true });
  window.addEventListener("resize", hide, { passive: true });

  // If responsive crosses breakpoint, hide
  mqDesktop.addEventListener?.("change", hide);
})();